
import { isEmpty, isValidEmail } from '@groww-tech-pvt/ella';
import { AxiosResponse } from 'axios';

import { STATE_CODES } from 'core/controllers/NomineeFormController/nomineeInfo.constants';
import { DATA_TYPE, SUBMISSION_MODE, DATA_POINT_STATUS, PAN_ERROR_MESSAGE, USER_NAME_ERROR_MESSAGE, MOBILE_UPDATE_ERROR_MESSAGE, EMAIL_UPDATE_ERROR_MESSAGE, JOURNEY_TYPES, GENERAL_ERROR_CONSTANTS, POLL_STATUS, DATA_SUBMISSION_STATUS, CONTENT_TYPES } from '../constants';
import { PanValidationResult, UserNameValidationResult, AddressFormData, AddressFormErrors, UserData, ProgressItem, MobileUpdateValidationResult, EmailUpdateValidationResult } from '../types';
import PollClient from './PollClient';


export const getMaskedPan = (pan: string) => {
    return pan ? pan.slice(0, 3) + 'XXXXXX' + pan.slice(-1) : '';
};

export const getCurrentStateOfUser = (userData: UserData) => {
    const isMobileVerified =
        !isEmpty(userData?.mobile_verified) && !isEmpty(userData?.mobile_number);

    if (!isMobileVerified) return 'MOBILE_VERIFICATION';
};

export const checkIfCosmosPollSuccess = (result: any) => {
    const pollStatus = result?.pollStatus?.toUpperCase();
    const dataSubmissionStatus = result?.dataSubmissionStatus?.toUpperCase();

    if (pollStatus === POLL_STATUS.SUCCESS || dataSubmissionStatus === DATA_SUBMISSION_STATUS.SUCCESS) {
      return true;
    }
    return false;
};

export const checkIfCosmosPollFailed = (result: any) => {
    const pollStatus = result?.pollStatus?.toUpperCase();
    const dataSubmissionStatus = result?.dataSubmissionStatus?.toUpperCase();

    if (pollStatus === POLL_STATUS.FAILED || dataSubmissionStatus === DATA_SUBMISSION_STATUS.REJECTED) {
      return true;
    }
    return false;
};

export const getEmailSubmissionErrorMessage = (error: unknown): string => {
    const err = error as {
      response?: { data?: { error?: { message?: string } }; status?: number };
      message?: string;
    };
    const serverMsg = err?.response?.data?.error?.message;
    if (serverMsg && typeof serverMsg === 'string' && serverMsg.trim()) {
      return serverMsg;
    }
    if (err?.response?.status && err.response.status >= 400) {
      return GENERAL_ERROR_CONSTANTS.GENERAL_ERROR;
    }
    const msg = err?.message;
    if (msg && /request failed with status code/i.test(msg)) {
      return GENERAL_ERROR_CONSTANTS.GENERAL_ERROR;
    }
    return GENERAL_ERROR_CONSTANTS.GENERAL_ERROR;
};


export const getUploadMimeType = (file: File): string => {
    if (file.type === CONTENT_TYPES.PDF || file.name.toLowerCase().endsWith('.pdf')) {
        return CONTENT_TYPES.PDF;
    }
    if (file.type && file.type.startsWith('image/')) {
        return CONTENT_TYPES.IMAGE;
    }
    return CONTENT_TYPES.IMAGE;
};


// Remove AUTO_FETCH_PAN mode from progress when auto-fetch fails
export const getUpdatedProgress = (
    progress: ProgressItem[] | null,
    dataType: typeof DATA_TYPE | string,
    submissionMode: typeof SUBMISSION_MODE | string
) => {
    if (!progress || !Array.isArray(progress) || !progress.length) return;

    const updatedProgress = progress.map((item: ProgressItem) => {
        if (item.type === dataType) {
            return {
                ...item,
                modes:
                    item.modes?.filter((mode: any) => mode.type !== submissionMode) || [],
                status: DATA_POINT_STATUS.NOT_SUBMITTED,
            };
        }
        return item;
    });

    return updatedProgress;
};

export const transformProgressForStore = (response: any) => {
    if (!response || typeof response !== 'object') {
        return undefined;
    }

    const { dataCollectionOrder, progress, screenConfigs } = response;

    const targetProgress = dataCollectionOrder || progress;

    if (!Array.isArray(targetProgress)) {
        return undefined;
    }

    const updatedProgress = targetProgress.map((dataPoint: any) => {
        const updatedModes = dataPoint.modes?.map((mode: any) => ({
            ...mode,
            screenConfig: screenConfigs?.[mode.configReference],
        }));

        return {
            ...dataPoint,
            modes: updatedModes,
            status: DATA_POINT_STATUS.NOT_SUBMITTED,
        };
    });

    return updatedProgress;
};

export const validateAccountNumber = (accountNumber: string) => {
    if (!accountNumber.trim()) {
        return 'Account number is required';
    }

    if (accountNumber.length < 8 || accountNumber.length > 18) {
        return 'Account number must be between 8-18 digits';
    }

    if (!/^\d+$/.test(accountNumber)) {
        return 'Account number should contain only digits';
    }

    return '';
};

// Validates old and new mobile numbers for the mobile update flow.
// Checks: non-empty, exactly 10 digits, no leading zero, and old !== new.
export const checkForMobileUpdateValidation = (
    oldMobile: string,
    newMobile: string
): MobileUpdateValidationResult => {
    const trimmedOld = oldMobile?.trim() ?? '';
    const trimmedNew = newMobile?.trim() ?? '';

    if (isEmpty(trimmedOld)) {
        return {
            isValid: false,
            oldMobileError: MOBILE_UPDATE_ERROR_MESSAGE.INVALID_MOBILE_NUMBER,
        };
    }

    // Indian mobile: exactly 10 digits, only numeric, cannot start with 0
    if (trimmedOld.length !== 10 || !/^\d+$/.test(trimmedOld) || trimmedOld[0] === '0') {
        return {
            isValid: false,
            oldMobileError: MOBILE_UPDATE_ERROR_MESSAGE.INVALID_MOBILE_NUMBER,
        };
    }

    if (isEmpty(trimmedNew)) {
        return {
            isValid: false,
            newMobileError: MOBILE_UPDATE_ERROR_MESSAGE.INVALID_MOBILE_NUMBER,
        };
    }

    if (trimmedNew.length !== 10 || !/^\d+$/.test(trimmedNew) || trimmedNew[0] === '0') {
        return {
            isValid: false,
            newMobileError: MOBILE_UPDATE_ERROR_MESSAGE.INVALID_MOBILE_NUMBER,
        };
    }

    // Prevent user from submitting the same number as both old and new
    if (trimmedOld === trimmedNew) {
        return {
            isValid: false,
            newMobileError: MOBILE_UPDATE_ERROR_MESSAGE.UPDATE_MOBILE_MATCH_ERROR,
        };
    }

    return { isValid: true };
};

/**
 * Resolves a state_code from STATE_CODES by matching the state name (case-insensitive).
 * Falls back to the provided fallback value (e.g. from the API) if no match is found.
 */
export const getStateCodeFromName = (stateName: string, fallback = ''): string => {
    if (!stateName) return fallback;

    const normalised = stateName.trim().toLowerCase();
    const match = STATE_CODES.find(s => s.label.toLowerCase() === normalised);

    return match?.value ?? fallback;
};

/**
 * Maps pincode/API fields to a canonical STATE_CODES label so dropdowns stay in sync.
 * Prefers `fallbackStateCode` when present; otherwise matches by name (any case); if the
 * state field looks like a 2-letter code, resolves via STATE_CODES value.
 */
export const normaliseAddressStateLabel = (
    stateName: string,
    fallbackStateCode = ''
): string => {
    const codeTrimmed = (fallbackStateCode ?? '').trim().toUpperCase();
    if (codeTrimmed) {
        const byCode = STATE_CODES.find(s => s.value.toUpperCase() === codeTrimmed);
        if (byCode) return byCode.label;
    }

    const trimmed = (stateName ?? '').trim();
    if (!trimmed) return '';

    const byLabel = STATE_CODES.find(
        s => s.label.toLowerCase() === trimmed.toLowerCase()
    );
    if (byLabel) return byLabel.label;

    if (/^[A-Za-z]{2}$/.test(trimmed)) {
        const byStateAsCode = STATE_CODES.find(
            s => s.value.toUpperCase() === trimmed.toUpperCase()
        );
        if (byStateAsCode) return byStateAsCode.label;
    }

    return trimmed;
};


// Validates old and new emails for the email update flow.
// Uses isValidEmail from ella for format check, case-insensitive match to prevent same email submission.
export const checkForEmailUpdateValidation = (
    oldEmail: string,
    newEmail: string
): EmailUpdateValidationResult => {
    const trimmedOld = oldEmail?.trim() ?? '';
    const trimmedNew = newEmail?.trim() ?? '';

    if (isEmpty(trimmedOld) || !isValidEmail(trimmedOld)) {
        return {
            isValid: false,
            oldEmailError: EMAIL_UPDATE_ERROR_MESSAGE.INVALID_EMAIL,
        };
    }

    if (isEmpty(trimmedNew) || !isValidEmail(trimmedNew)) {
        return {
            isValid: false,
            newEmailError: EMAIL_UPDATE_ERROR_MESSAGE.INVALID_EMAIL,
        };
    }

    // Case-insensitive comparison since email addresses are not case-sensitive
    if (trimmedOld.toLowerCase() === trimmedNew.toLowerCase()) {
        return {
            isValid: false,
            newEmailError: EMAIL_UPDATE_ERROR_MESSAGE.UPDATE_EMAIL_MATCH_ERROR,
        };
    }

    return { isValid: true };
};

// This function encapsulates the boilerplate for configuring and starting a PollClient.
export const initiateCosmosPolling = <T extends AxiosResponse<any>>({
    pollingApiFn, // The specific API function for polling (e.g., initProgressPoll, initSubmissionPoll)
    maxAttempts,
    pollInterval,
    continuePollingCondition, // Condition function for the PollClient
    onPollSuccess, // Callback for when the polling condition becomes false
    onPollFailed, // Callback for network/API errors during polling
    onPollExpire, // Callback for polling timeout
}: {
    pollingApiFn: () => Promise<T>;
    maxAttempts: number;
    pollInterval: number;
    continuePollingCondition: (res: T) => boolean;
    onPollSuccess: (res: T) => void;
    onPollFailed: (err: Error) => void;
    onPollExpire: () => void;
}): PollClient<T> => {
    const pollClient = new PollClient<T>();

    pollClient
        .setPollingLimit(maxAttempts)
        .setPollingInterval(pollInterval)
        .setPollingFunction(pollingApiFn)
        .setContinuePollingCondition(continuePollingCondition)
        .setPollSuccessCallback(onPollSuccess)
        .setPollFailedCallback(onPollFailed)
        .setPollExpireCallback(onPollExpire);

    pollClient.startPoll();

    return pollClient;
};

/**
 * This function adds a script element,
 * It takes two parameters
 *@param scriptPath[string]- src path for script
 *@param scriptId[string] - id for the script
 */
export const loadExternalScript = (scriptPath: string, scriptId: string) => {
    const existingScript = document.getElementById(scriptId);

    if (!existingScript) {
        const script = document.createElement('script');

        script.src = scriptPath;
        script.id = scriptId;
        document.body.appendChild(script);
    }
};

/**
 * This function removes the script element if it exists, taking id from the param
 * @param scriptId[string] - id for the script
 */
export const removeExternalScript = (scriptId: string) => {
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
        existingScript.remove();
    }
};

export const checkTypeOfPan = (pan: string): string => {
    const panValue = pan.toUpperCase().trim();
    const panTypeChar = panValue[3];
    return panTypeChar;
};

export const checkForPanValidation = (pan: string): PanValidationResult => {
    if (!pan) {
        return { isValid: false, message: PAN_ERROR_MESSAGE.PAN_CANNOT_BE_EMPTY };
    }

    const panValue = pan.toUpperCase().trim();

    // Check length
    if (panValue.length !== 10) {
        return {
            isValid: false,
            message: PAN_ERROR_MESSAGE.PAN_MUST_BE_10_CHARACTERS_LONG,
        };
    }

    // Regex for PAN: 5 letters + 4 digits + 1 letter
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    if (!panRegex.test(panValue)) {
        return {
            isValid: false,
            message: PAN_ERROR_MESSAGE.PAN_FORMAT_INVALID,
        };
    }

    // Optional: Check 4th character for PAN type
    const panTypeChar = panValue[3];
    const validPanTypes = ['P', 'C', 'H', 'A', 'B', 'G', 'J', 'L', 'F', 'T']; // Standard PAN holder types
    if (!validPanTypes.includes(panTypeChar)) {
        return {
            isValid: false,
            message: PAN_ERROR_MESSAGE.INVALID_PAN_TYPE_CHARACTER,
        };
    }

    // Optional: Check 5th character is letter (first letter of surname/company)
    const fifthChar = panValue[4];
    if (!/[A-Z]/.test(fifthChar)) {
        return {
            isValid: false,
            message: PAN_ERROR_MESSAGE.INVALID_PAN_HOLDER_IDENTIFIER,
        };
    }

    return { isValid: true };
};

export const validateUserName = (
    username: string
): UserNameValidationResult => {
    if (!username) {
        return {
            isValid: false,
            message: USER_NAME_ERROR_MESSAGE.NAME_CANNOT_BE_EMPTY,
        };
    }

    const trimmed = username.trim();

    if (trimmed.length < 2) {
        return {
            isValid: false,
            message: USER_NAME_ERROR_MESSAGE.NAME_MUST_BE_AT_LEAST_2_CHARACTERS_LONG,
        };
    }

    if (trimmed.length > 50) {
        return {
            isValid: false,
            message: USER_NAME_ERROR_MESSAGE.NAME_CANNOT_EXCEED_50_CHARACTERS,
        };
    }

    // No consecutive dots/underscores
    if (/(\.\.|__)/.test(trimmed)) {
        return {
            isValid: false,
            message:
                USER_NAME_ERROR_MESSAGE.NAME_CANNOT_CONTAIN_CONSECUTIVE_DOTS_OR_UNDERSCORES,
        };
    }

    // Cannot start or end with dot/underscore
    if (/^[._]|[._]$/.test(trimmed)) {
        return {
            isValid: false,
            message:
                USER_NAME_ERROR_MESSAGE.NAME_CANNOT_START_OR_END_WITH_DOT_OR_UNDERSCORE,
        };
    }

    return { isValid: true };
};

export const getDocumentTitle = (docType: string) => {
    switch (docType) {
        case 'AADHAAR':
            return 'Aadhaar Card';
        case 'DRIVING_LICENSE':
            return 'Driving License';
        case 'VOTER_ID':
            return 'Voter ID';
        case 'PASSPORT':
            return 'Passport';
        default:
            return 'Document';
    }
};

export function isValidPan(panNumber: string) {
    const validPanRegex = /^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/;
    /*
    pan number with letter c, f and e on fourth place in pan number are also valid PAN
    but these belong to HUFs and Companies. which are not allowed for investment on groww
  */
    const notAllowedPanRegex =
        /^([a-zA-Z]{3})([CcFfEe]{1})([a-zA-Z]{1})(\d{4})([a-zA-Z]{1})$/;

    return validPanRegex.test(panNumber) && !notAllowedPanRegex.test(panNumber);
}

export type AddressFormValidateOptions = {
    /** When true (e.g. DL / passport), expiry must be non-empty. */
    requireExpiry?: boolean;
};

export const validateForm = (
    formData: AddressFormData,
    options?: AddressFormValidateOptions
): AddressFormErrors => {
    const newErrors: AddressFormErrors = {};

    if (!formData.addressLine1.trim()) {
        newErrors.addressLine1 = 'Address line 1 is required';
    }

    if (!formData.addressLine2.trim()) {
        newErrors.addressLine2 = 'Address line 2 is required';
    }

    if (!formData.city.trim()) {
        newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
        newErrors.state = 'State is required';
    }

    if (!formData.pinCode.trim()) {
        newErrors.pinCode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pinCode)) {
        newErrors.pinCode = 'Enter valid 6-digit pincode';
    }

    if (options?.requireExpiry && !formData.expiry?.trim()) {
        newErrors.expiry = 'Expiry date is required';
    }

    return newErrors;
};

// Validation functions
export const validateDateOfBirth = (dob: string, journeyType: string) => {
    if (!dob.trim()) {
        return 'Date of birth is required';
    }

    const dobDate = new Date(dob);
    const today = new Date();

    // Check if date is valid
    if (isNaN(dobDate.getTime())) {
        return 'Please enter a valid date';
    }

    // Check if date is in the future
    if (dobDate > today) {
        return 'Date of birth cannot be in the future';
    }

    // Calculate age
    const age = today.getFullYear() - dobDate.getFullYear();
    const monthDiff = today.getMonth() - dobDate.getMonth();
    const dayDiff = today.getDate() - dobDate.getDate();

    const actualAge =
        age - (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? 1 : 0);

    // Check minimum age (18 years)
    if (actualAge < 18 && journeyType !== JOURNEY_TYPES.HUF_ONBOARDING) {
        return 'You must be at least 18 years old to proceed';
    }

    // Check maximum reasonable age (120 years)
    if (actualAge > 120) {
        return 'Please enter a valid date of birth';
    }

    return '';
};

export const formatDateForAPI = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

export const validateName = (name: string) => {
    if (!name.trim()) {
        return "Father's name is required";
    }
    if (name.trim().length < 2) {
        return 'Name must be at least 2 characters long';
    }
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
        return 'Name should contain only letters and spaces';
    }
    return '';
};

const getPrevMonth = (months: number) => {
    return new Date(
        new Date().setMonth(new Date().getMonth() - months)
    ).toLocaleString('default', { month: 'short' });
};

const getCurrFinancialYYYY = (offset: number) => {
    return new Date(
        new Date().setFullYear(new Date().getFullYear() + offset)
    ).getFullYear();
};

export const INCOME_PROOF = [
    {
        value: 'BANK_STATEMENT',
        label: 'Bank statement',
        placeHolder: `Min. one transaction of ₹5,000 in last 6 months`,
        proofTitle: {
            h1: 'Upload bank statement',
            uploadBannerTitle: 'Upload bank statement',
            h2: `Statement duration between ${getPrevMonth(6)} - ${getPrevMonth(1)}`,
            h3: 'Atleast one transaction of ₹5000 or more',
            h4: `Your full name should be visible`,
        },
    },
    {
        value: 'SALARY_SLIP',
        label: 'Salary Slip',
        placeHolder: `Latest with min. ₹7,500 monthly income`,
        proofTitle: {
            h1: `Upload salary slip`,
            uploadBannerTitle: `Upload salary slip`,
            h2: `Salary Slip should be of ${getPrevMonth(1)} Month`,
            h3: `Your full name should be visible`,
        },
    },
    {
        value: 'ITR_ACKNOWLEDGEMENT',
        label: 'ITR Acknowledgement',
        placeHolder: `Latest with annual income ≥ ₹90,000`,
        proofTitle: {
            h1: `Upload Latest ITR Acknowledgement`,
            uploadBannerTitle: `Upload latest ITR with annual income ≥ INR 90000`,
            h2: `Assessment year should be ${getCurrFinancialYYYY(1)}`,
            h3: `Your full name should be visible`,
        },
    },
    {
        value: 'FORM16B',
        label: 'Form 16B',
        placeHolder: `Latest with annual income ≥ ₹90,000`,
        proofTitle: {
            h1: `Upload Latest Form 16`,
            uploadBannerTitle: `Upload latest Form 16 with annual income ≥ INR 90000`,
            h2: `Assessment year should be ${getCurrFinancialYYYY(1)}`,
            h3: `Your full name should be visible`,
        },
    },
];
